// src/features/statistics/group-breakdowns.tsx
import { Kicker } from "@/components/iris/kicker";
import type { StatisticsSnapshot } from "@/lib/types";
import {
  CHART_SEMANTIC,
  ChartCard,
  DonutWithLegend,
  SimpleBar,
  chartColor,
} from "./chart-primitives";

interface GroupBreakdownsProps {
  breakdowns: StatisticsSnapshot["group_breakdowns"];
}

export function GroupBreakdowns({ breakdowns }: GroupBreakdownsProps) {
  return (
    <>
      <Kicker style={{ marginTop: 32, marginBottom: 12 }}>Group Distribution</Kicker>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        {/* By Platform — Donut + side legend with exact counts (IR-1046 ④) */}
        <ChartCard
          title="By Platform"
          info="Profiler-originated sources are grouped under one 'profiler' slice; the long tail collapses into 'Other'."
        >
          <DonutWithLegend
            data={breakdowns.by_platform}
            nameKey="platform"
            dataKey="count"
            totalLabel="Groups"
          />
        </ChartCard>

        {/* By Classification — Bar chart (orange for warn/classification) */}
        <ChartCard title="By Classification">
          <SimpleBar
            data={breakdowns.by_classification}
            xKey="classification"
            yKey="count"
            color={chartColor(4)}
          />
        </ChartCard>

        {/* By Message Volume — Histogram with 6 buckets (purple for volume) */}
        <ChartCard title="By Message Volume">
          <SimpleBar
            data={breakdowns.by_message_volume}
            xKey="bucket"
            yKey="count"
            color={chartColor(1)}
          />
        </ChartCard>

        {/* By Status — Donut + side legend with exact counts (IR-1046 ④) */}
        <ChartCard title="By Status">
          <DonutWithLegend
            data={breakdowns.by_status}
            nameKey="status"
            dataKey="count"
            totalLabel="Groups"
          />
        </ChartCard>

        {/* By Last Active — Bar chart with time buckets (orange for warn/last-active) */}
        <ChartCard
          title="By Last Active"
          subtitle={'"never" = groups where last activity was never recorded by the pipeline'}
        >
          <SimpleBar
            data={breakdowns.by_last_active}
            xKey="bucket"
            yKey="count"
            color={CHART_SEMANTIC.warn}
          />
        </ChartCard>

        {/* Top 10 by Members — Horizontal bar chart (magenta for top-N) */}
        <ChartCard title="Top 10 by Members">
          <SimpleBar
            data={breakdowns.top_by_members.slice(0, 10)}
            xKey="group_name"
            yKey="member_count"
            layout="vertical"
            color={chartColor(0)}
            leftMargin={140}
            tickFontSize={10}
          />
        </ChartCard>
      </div>
    </>
  );
}
